// ForgotPassword.jsx
// Modern password reset page with professional design

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { styles } from './styles';
import { EmailIcon, ArrowLeftIcon, CheckCircleIcon } from '../../components/Icons';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const validateEmail = (email) => {
    if (!email) {
      return 'Please enter your email address.';
    }
    if (!email.endsWith('@ilstu.edu')) {
      return 'Please use a valid ISU email address (@ilstu.edu).';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + '/reset-password',
        handleCodeInApp: false
      });
      setSuccess(true);
    } catch (err) {
      // Handle specific error cases
      if (err.code === 'auth/user-not-found') {
        setError('No account exists with this email address. Please check your email or sign up.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many reset attempts. Please try again later.');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (success) {
    return (
      <div style={styles.container} className="forgot-password-container">
        <div style={styles.backgroundDecoration}></div>
        
        <div style={styles.card}>
          <div style={styles.successIconContainer}>
            <CheckCircleIcon size={64} color="#22C55E" />
          </div>
          
          <h1 style={styles.successTitle}>Check Your Email</h1>
          
          <p style={styles.successText}>
            We've sent a password reset link to:
          </p>
          
          <div style={styles.emailDisplay}>
            {email}
          </div>
          
          <p style={styles.instructionText}>
            Click the link in the email to reset your password. The link will expire in 1 hour.
          </p>
          
          <div style={styles.tipsContainer}>
            <h3 style={styles.tipsTitle}>💡 Helpful Tips:</h3>
            <ul style={styles.tipsList}>
              <li>Check your spam/junk folder if you don't see the email</li>
              <li>The email should arrive within a few minutes</li>
              <li>Make sure to check the correct ISU email inbox</li>
            </ul>
          </div>
          
          <button
            onClick={handleBackToLogin}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 24px rgba(206, 17, 38, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 16px rgba(206, 17, 38, 0.2)';
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="forgot-password-container">
      <div style={styles.backgroundDecoration}></div>
      
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <div style={styles.logoIcon}>
              <img 
                src="/favicon_io/android-chrome-192x192.png" 
                alt="Redbird Markets Logo" 
                style={styles.logoImage}
              />
            </div>
          </div>
          <h1 style={styles.title}>Reset Your Password</h1>
          <p style={styles.subtitle}>
            Enter your ISU email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel} htmlFor="email">
              ISU Email Address
            </label>
            <div style={styles.inputWrapper}>
              <EmailIcon 
                size={20} 
                color="#666" 
                style={styles.inputIcon}
              />
              <input
                id="email"
                type="email"
                placeholder="your.name@ilstu.edu"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === 'email' ? styles.inputFocused : {}),
                  ...(error ? styles.inputError : {})
                }}
                className="forgot-password-input"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              ...(loading ? styles.submitButtonDisabled : {})
            }}
            className="forgot-password-submit"
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px rgba(206, 17, 38, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(206, 17, 38, 0.2)';
              }
            }}
          >
            {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
          </button>
        </form>

        {/* Back to Login Link */}
        <div style={styles.backLinkContainer}>
          <button
            onClick={handleBackToLogin}
            style={styles.backLink}
            className="forgot-password-back-link"
            onMouseEnter={(e) => {
              e.target.style.color = '#A00E1E';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#CE1126';
            }}
          >
            <ArrowLeftIcon size={16} />
            <span>Back to Login</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
